import { fetchBaseQuery } from "@reduxjs/toolkit/dist/query";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import { getApiHostUrl, getDefaultHeaders } from "../helpers/apiHelpers";
import { getKindeToken } from "./kindeTokenProvider";
import { accountIdSelector } from "./slices/account/selectors";
import { selectViewAsProvider } from "./slices/faView";
import { operatorIdSelector, defaultCountrySelector } from "./slices/operator/selectors";
import { setSubscriptionExpired } from "./slices/ui/uiSlice";
import type { RootState } from "./store";

// Form data related requests skips header setting
// https://github.com/reduxjs/redux-toolkit/issues/2287#issuecomment-1111213377
const FORMDATA_ENDPOINTS = [
  "uploadAsset",
  "addAccreditation",
  "editAccreditation",
  "updateAvatar",
  "addBookingOfferDocument",
  "addJobAttachment",
  "addAvatar",
  "addVehicleDocument",
  "addDriverDocument",
  "addDriverIncidentDocument",
  "uploadDocument",
  // Client + guest booking attachment uploads (DEV-1252) — multipart FormData. Without this the
  // default application/json header is stamped on the body, the browser drops the multipart
  // boundary, and the proxy's json body-parser (100 KB) rejects the image with 413.
  "uploadClientBookingAttachment",
  "uploadGuestBookingAttachment",
];

/**
 * Raw base query that uses Kinde access token for authentication
 * Token is fetched on-demand for each request (not stored in Redux)
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: getApiHostUrl(),
  prepareHeaders: async (headers, { endpoint }) => {
    // Get Kinde token for each request
    const token = await getKindeToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Set default headers for non-form-data requests
    if (!FORMDATA_ENDPOINTS.includes(endpoint)) {
      headers = getDefaultHeaders(headers);
    }
    return headers;
  },
});

/**
 * True when the API rejected the request because the connected operator's subscription has lapsed.
 * The backend returns 403 with `{ message: "A valid subscription is required..." }`. Extracted as a
 * pure helper so the matching rule is unit-testable without exercising the network.
 */
export const isSubscriptionExpiredError = (error?: FetchBaseQueryError): boolean => {
  if (!error || error.status !== 403) return false;
  const message = (error.data as { message?: string } | undefined)?.message;
  return typeof message === "string" && message.includes("A valid subscription is required");
};

/**
 * Base query wrapper. Delegates to `rawBaseQuery` and globally intercepts the "subscription lapsed"
 * 403 so the SubscriptionExpired blocking modal is raised no matter which endpoint tripped it — every
 * derived base query below funnels through this wrapper, so interception is app-wide.
 */
export const baseQuery: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (isSubscriptionExpiredError(result.error)) {
    api.dispatch(setSubscriptionExpired(true));
  }
  return result;
};

/**
 * Base query with provider country in URL
 * Example: /v3/provider/{operatorId}/config/{country}/...
 */
export const baseQueryWithProviderCountry: (endpoint?: string) => typeof baseQuery =
  (endpoint = "config") =>
  async (args, api, extraOptions) => {
    const country = defaultCountrySelector(api.getState());
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/provider/${operatorId}${endpoint ? `/${endpoint}` : ""}/${country}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query with provider in URL
 * Example: /v3/provider/{operatorId}/...
 */
export const baseQueryWithProvider: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/provider/${operatorId}/${endpoint}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query with endpoint first, then provider in URL
 * Example: /v3/{endpoint}/{operatorId}/...
 */
export const baseQueryWithProviderEndpointFirst: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const operatorId = operatorIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/${endpoint}/${operatorId}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Base query without provider in URL
 * Example: /v3/{endpoint}/...
 */
export const baseQueryWithoutProvider: (endpoint?: string) => typeof baseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/${endpoint}${urlEnd}`;
    const adjustedArgs = typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
    return baseQuery(adjustedArgs, api, extraOptions);
  };

/**
 * Args accepted by the client/account base queries. Same as the standard
 * fetch args, plus an optional `accountId` that overrides the active account
 * in the URL (e.g. an operator acting on a managed client's `:clientId`).
 * When omitted, the active account from state is used — self-service unchanged.
 */
export type TargetedFetchArgs = FetchArgs & { accountId?: string };
type TargetedBaseQuery = BaseQueryFn<string | TargetedFetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>;

/**
 * Base query with client in URL
 * Example: /v3/client/{accountId}/{endpoint}/...
 * Pass `{ accountId }` in the request args to target a specific client.
 */
export const baseQueryWithClient: (endpoint?: string) => TargetedBaseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const override = typeof args === "string" ? undefined : args.accountId;
    const accountId = override ?? accountIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `/v3/client/${accountId}/${endpoint}${urlEnd}`;
    if (typeof args === "string") {
      return baseQuery(adjustedUrl, api, extraOptions);
    }
    const { accountId: _ignored, ...rest } = args;
    return baseQuery({ ...rest, url: adjustedUrl }, api, extraOptions);
  };

/**
 * Base query with account management in URL
 * Example: /v3/account-management/{accountId}/...
 * Pass `{ accountId }` in the request args to target a specific account (e.g. an operator editing a
 * managed client). When omitted, the active account from state is used — self-service unchanged.
 */
export const baseQueryWithAccount: (endpoint?: string) => TargetedBaseQuery =
  (endpoint = "") =>
  async (args, api, extraOptions) => {
    const override = typeof args === "string" ? undefined : args.accountId;
    const accountId = override ?? accountIdSelector(api.getState());
    const urlEnd = typeof args === "string" ? args : args.url;
    let adjustedUrl = `/v3/account-management/${accountId}/${endpoint}${urlEnd}`;
    // FA "View as operator" preview — a query param (not a custom header, which would force a CORS
    // preflight) tells web-app-v2 to return the operator's own subscription-scoped permissions.
    if (selectViewAsProvider(api.getState() as RootState)) {
      adjustedUrl += `${adjustedUrl.includes("?") ? "&" : "?"}viewAsOperator=1`;
    }
    if (typeof args === "string") {
      return baseQuery(adjustedUrl, api, extraOptions);
    }
    // Strip the on-behalf accountId from the args so it never leaks into the request body.
    const { accountId: _ignored, ...rest } = args;
    return baseQuery({ ...rest, url: adjustedUrl }, api, extraOptions);
  };
